import dayjs from 'dayjs';

// The query-param date format understood by populateFilters.js / updateUrl.js
const URL_DATE_FORMAT = 'MM-DD-YY HH:mm:ss';

export const FREQUENCIES = [
  { label: 'Daily', value: 'daily' },
  { label: 'Weekly', value: 'weekly' },
  { label: 'Monthly', value: 'monthly' },
];

export const DAYS_OF_WEEK = [
  { label: 'Monday', value: 0 },
  { label: 'Tuesday', value: 1 },
  { label: 'Wednesday', value: 2 },
  { label: 'Thursday', value: 3 },
  { label: 'Friday', value: 4 },
  { label: 'Saturday', value: 5 },
  { label: 'Sunday', value: 6 },
];

// Day 29-31 are excluded so a monthly alert fires in every month.
export const DAYS_OF_MONTH = Array.from({ length: 28 }, (_, i) => ({
  label: `${i + 1}`,
  value: i + 1,
}));

const LOOKBACK_BY_FREQUENCY = { daily: 1, weekly: 7, monthly: 30 };

export const lookbackDays = (frequency) =>
  LOOKBACK_BY_FREQUENCY[frequency] ?? LOOKBACK_BY_FREQUENCY.weekly;

export const getBrowserTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
};

// A short, curated list plus whatever the browser reports, so the select is
// usable without pulling in a full IANA database.
export const TIMEZONE_OPTIONS = (() => {
  const zones = [
    'UTC',
    'Asia/Karachi',
    'Asia/Kolkata',
    'Asia/Shanghai',
    'Asia/Tokyo',
    'Europe/London',
    'Europe/Berlin',
    'Europe/Paris',
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'America/Mexico_City',
    'America/Sao_Paulo',
    'Australia/Sydney',
  ];
  const browser = getBrowserTimezone();
  if (!zones.includes(browser)) zones.unshift(browser);
  return zones.map((z) => ({ label: z, value: z }));
})();

export const describeSchedule = (config) => {
  if (!config) return '-';
  const { frequency, day_of_week, day_of_month, send_time, timezone } = config;
  const time = (send_time || '').slice(0, 5);
  const parts = [];

  if (frequency === 'weekly') {
    const day = DAYS_OF_WEEK.find((d) => d.value === day_of_week);
    parts.push(`Weekly · ${day ? day.label : 'Monday'}`);
  } else if (frequency === 'monthly') {
    parts.push(`Monthly · day ${day_of_month || 1}`);
  } else {
    parts.push('Daily');
  }

  if (time) parts.push(time);
  if (timezone) parts.push(timezone);
  return parts.join(' · ');
};

/**
 * Maps a stored filter blob back onto the query-param keys that
 * populateFilters.js reads, so AdvanceFilters can be re-hydrated for editing.
 *
 * The saved config intentionally holds no date range (each report covers a
 * rolling window), so startTime/endTime are synthesised from the frequency.
 */
export const filtersToQuery = (filters = {}, frequency = 'weekly') => {
  const end = dayjs();
  const start = end.subtract(lookbackDays(frequency), 'day');

  const query = {
    taskId: filters.task,
    deviceId: filters.devices,
    startTime: start.format(URL_DATE_FORMAT),
    endTime: end.format(URL_DATE_FORMAT),
  };

  // populateFilters no-ops entirely unless both of these are present.
  if (!query.taskId || !query.deviceId) return null;

  const optional = {
    cycle_type: filters.cycle_type,
    tags: filters.tags,
    min_cycle_time_in_bin: filters.min_cycle_time_in_bin,
    max_cycle_time_in_bin: filters.max_cycle_time_in_bin,
    start_time: filters.start_time,
    end_time: filters.end_time,
    missed_steps: filters.missed_steps,
    interventions: filters.interventions,
    modelVersion: filters.model_version,
    vt: filters.virtualTask,
    meta_key: filters.meta_key,
    meta_value: filters.meta_value,
    meta_comparison_operator: filters.meta_comparison_operator,
  };

  Object.entries(optional).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      query[key] = value;
    }
  });

  if (filters.exclude_timeout_cycles !== undefined) {
    query.exclude_timeout_cycles = filters.exclude_timeout_cycles;
  }

  return query;
};

/**
 * Strips everything that must not be persisted from an AdvanceFilters
 * getPayload() result: the date range (rolling window instead), the org
 * (derived server-side) and the transient trace-navigation fields.
 */
export const toStoredFilters = (payload) => {
  /* eslint-disable no-unused-vars */
  const {
    datetime_start,
    datetime_end,
    organization,
    cyclesFromParams,
    runId,
    step_index,
    isComparing,
    c_start_date,
    c_end_date,
    c_devices,
    ...filters
  } = payload;
  /* eslint-enable no-unused-vars */
  return filters;
};
